import get from 'lodash/get';
import find from 'lodash/find';
import { createSelector } from 'reselect';

import * as constants from '@biw/contests-constants';
import { programs } from '@biw/contests-core';
import { ProgramModel } from '@biw/contests-core/lib/dux/programs';

import * as routeTypes from 'dux/router/types';

import { ROOT_KEY } from './index';

const {
    PVS__GOAL_SELECTION,
    PVS__GOAL_SELECTED,
    PVS__NO_GOAL_SELECTED,
    PVS__PROGRESS_LOADED_NO_ISSUANCE,
    PVS__FINAL_ISSUANCE,
} = constants;

const DEFAULT_STATUS_PAX_PRIORITY_LIST = [
    PVS__GOAL_SELECTION,
    PVS__GOAL_SELECTED,
    PVS__NO_GOAL_SELECTED,
    PVS__PROGRESS_LOADED_NO_ISSUANCE,
    PVS__FINAL_ISSUANCE,
];

const DEFAULT_STATUS_MANAGER_PRIORITY_LIST = [
    PVS__GOAL_SELECTION,
    PVS__GOAL_SELECTED,
    PVS__PROGRESS_LOADED_NO_ISSUANCE,
    PVS__FINAL_ISSUANCE,
];

const selectorRole = /selector/i;
const isManager = (role: string) => !selectorRole.test( role );
const EMPTY_OBJ = {};

export const getCurrentRoute = createSelector(
    (state) => get( state, `location.pathname` ),
    (state) => get( state, 'location.routesMap') || EMPTY_OBJ,
    ( currentRoutePath, routes ) => find( routes, (r) => r.path === currentRoutePath ),
);

export const getRouterIsInitialized = (state: any) => get( state, `${ROOT_KEY}.routerIsInitialized` ) || false;

export const getCurrentRouteType = (state: any) => get( state, 'location.type');
export const getCurrentRouteRequiresAuth = createSelector(
    getCurrentRouteType,
    ( routeType ) => get( routeType, 'requiresAuth', true ),
);
export const getLoadedRoute = (state: any) => get( state, `${ROOT_KEY}.loadedRoute`, 'DEFAULT_ROUTE' );

export const getComponentIsLoaded = ( state: any, route: string ) => get( state, `${ROOT_KEY}.loadedComponents.${route}`, false );
const internalGetRouteIsLoading = (state: any) => get( state, `${ROOT_KEY}.routeIsLoading`, false );
export const getRouteIsLoading = createSelector(
    internalGetRouteIsLoading,
    getLoadedRoute,
    getCurrentRouteType,
    ( routeIsLoading, loadedRoute, currentRoute ) => routeIsLoading === true || loadedRoute !== currentRoute,
);

export const getActiveProgramViewId = createSelector(
    (state: any ) => get(state, 'location.payload') || EMPTY_OBJ,
    (payload) => payload.programViewId || '',
);

export function getDefaultRouteForProgram( program: ProgramModel ) {
    if ( !program ) {
        return routeTypes.PROFILE;
    } else if ( isManager( program.role ) ) {
        return routeTypes.MANAGER_PROGRESS;
    } else if ( !program.programViewStatus ) {
        console.error( `No programViewStatus found for ${program.programViewId}` );
        return routeTypes.ERROR;
    }

    return program.programViewStatus === PVS__GOAL_SELECTION ?
        routeTypes.WELCOME : routeTypes.PROGRESS;
}

export const getDefaultProgramView = createSelector(
    programs.selectors.getProgramViews,
    ( programList ) => {
        let programView = programList
            .filter( (program) => !isManager( program.role ) )
            .find( (program) => DEFAULT_STATUS_PAX_PRIORITY_LIST.includes( program.programViewStatus ) );

        if ( !programView ) {
            programView = programList
                .filter( (program) => isManager( program.role ) )
                .find( (program) => DEFAULT_STATUS_MANAGER_PRIORITY_LIST.includes( program.programViewStatus ) );
        }

        return programView || programList[ 0 ];
    },
);

export const getActiveProgram = (state: any) => programs.selectors.getProgram( state, getActiveProgramViewId( state ) );
export const getScroll = (state: any) => state[ROOT_KEY].scroll || '';
